/**
 * The app's toast, wrapped so a failure never disappears on its own.
 *
 * Field report: "For the 'failed to fetch' notice, can you make it last longer?
 * It's one of those that happens for a brief moment and if I wasn't paying
 * attention I might have missed that it didn't send the first time."
 *
 * She's right, and the rule is broader than one message: a success can fade,
 * because the thing you wanted happened and the screen already shows it. A
 * failure is a piece of work still owed — it stays until a person dismisses it,
 * which is also the receipt that they saw it.
 *
 * So `toast.error` and `toast.warning` never auto-dismiss, whatever the call
 * site asks for. Everything else behaves exactly as sonner always did. Import
 * `toast` from here rather than from 'sonner' (an eslint rule enforces it) so
 * this holds everywhere without 667 call sites having to remember.
 *
 * The same chokepoint catches a second class of message no user should ever
 * read: "Loading chunk 9981 failed", which means a deploy landed while their
 * page was open. Those become a plain-English notice and a reload rather than
 * a chunk filename. See ./chunkRecovery.
 */

import { toast as sonner } from 'sonner';
import { isChunkLoadError, recoverFromChunkError, CHUNK_RECOVERY_MESSAGE } from './chunkRecovery';

type Sonner = typeof sonner;
type Opts = Parameters<Sonner['error']>[1];

/** Stays put, with an X, no matter what the call site passed. */
function untilDismissed(opts?: Opts): Opts {
  return { ...(opts ?? {}), duration: Infinity, closeButton: true };
}

export const toast: Sonner = new Proxy(sonner, {
  get(target, prop, receiver) {
    if (prop === 'error' || prop === 'warning') {
      const fn = Reflect.get(target, prop, receiver) as Sonner['error'];
      return (message: Parameters<Sonner['error']>[0], opts?: Opts) => {
        // A deploy landed while this page was open, and some lazy-loaded
        // module is now missing. Hundreds of call sites do
        // `toast.error(e.message)` inside a catch, which swallows the error
        // before the global handler can see it — so the user used to get a
        // chunk filename and a dead button. Catching it here covers every one
        // of those call sites without any of them having to know.
        if (isChunkLoadError(message)) {
          const shown = fn(CHUNK_RECOVERY_MESSAGE, untilDismissed(opts));
          // Long enough to be read, short enough not to feel stuck.
          recoverFromChunkError(2500);
          return shown;
        }
        return fn(message, untilDismissed(opts));
      };
    }
    return Reflect.get(target, prop, receiver);
  },
});

export default toast;
